import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import {
  type StatusInventoryEditorField,
  type StatusInventorySectionDetail,
  type StatusInventorySectionId,
  type StatusInventorySectionSummary,
  type StatusInventoryTableColumn,
  type StatusInventoryTableRow,
  type StatusInventoryViewProps,
  type ColumnFiltersState,
  type PaginationState,
  type SortingState,
} from '@workspace/silk-ui';
import { toast } from '@workspace/ui';
import { useScope } from '@workspace/scope-context';
import { useNavigation } from '@/context/NavigationContext';

import {
  createStatusConfig,
  deleteStatusConfig,
  discoverStatusDevice,
  getStatusManagedServiceSummary,
  listStatusConfigPage,
  restoreStatusLink,
  StatusInventoryError,
  suppressStatusLink,
  type StatusDevice,
  type StatusDeviceComponent,
  type StatusDeviceDiscoveryPayload,
  type StatusDeviceDiscoveryResult,
  type StatusDeviceType,
  type StatusDeviceTypeSNMPOverlay,
  type StatusInterface,
  type StatusLinkConfig,
  type StatusSNMPAuthProfile,
  type StatusSite,
  type StatusConfigListOptions,
  type StatusConfigResource,
  updateStatusConfig,
  type WritableStatusConfigResource,
} from '@/lib/status-inventory';
import { useStatusInventoryAccess } from '@/lib/useStatusInventoryAccess';

type Draft = Record<string, string | boolean>;
type EditableStatusInventorySectionId = Exclude<StatusInventorySectionId, 'entities'>;
type TakeoverMode = 'editor' | 'device-detail' | null;
type ControllerRow = Omit<StatusInventoryTableRow, 'actionLabel'> & { actionLabel?: string };
type ControllerSection = StatusInventorySectionSummary & {
  columns: StatusInventoryTableColumn[];
  rows: ControllerRow[];
  emptyTitle: string;
  emptyDescription: string;
  detail?: StatusInventorySectionDetail | null;
};

const VALID_SECTIONS = new Set<StatusInventorySectionId>(['hubs', 'models', 'devices', 'entities', 'auth', 'overlays', 'links']);

function parseSectionFromPath(path: string): StatusInventorySectionId {
  const segment = path.split('?')[0].replace(/\/$/, '').split('/').pop() ?? '';
  return VALID_SECTIONS.has(segment as StatusInventorySectionId)
    ? (segment as StatusInventorySectionId)
    : 'devices';
}

const SECTION_RESOURCE: Record<EditableStatusInventorySectionId, WritableStatusConfigResource> = {
  hubs: 'sites',
  models: 'device-types',
  devices: 'devices',
  auth: 'snmp-auth-profiles',
  overlays: 'device-type-snmp-overlays',
  links: 'links',
};

const SECTION_LIST_RESOURCE: Record<StatusInventorySectionId, StatusConfigResource> = {
  hubs: 'sites',
  models: 'device-types',
  devices: 'devices',
  auth: 'snmp-auth-profiles',
  entities: 'device-components',
  overlays: 'device-type-snmp-overlays',
  links: 'links',
};

const SECTION_SORT_FIELDS: Record<StatusInventorySectionId, Record<string, string>> = {
  hubs: { slug: 'slug', location: 'location', external: 'external_ref' },
  models: { vendor: 'vendor', match: 'model_match', overlays: 'active_overlay_count' },
  devices: { site: 'site_display_name', type: 'device_type_display_name', address: 'management_address' },
  auth: { version: 'snmp_version', active: 'active' },
  entities: {
    device: 'device_display_name',
    kind: 'component_kind',
    sensor: 'sensor_type',
    value: 'sensor_value',
    status: 'sensor_oper_status',
  },
  overlays: { model: 'device_type_display_name', module: 'module_name', version: 'version' },
  links: { source: 'source', target: 'target', state: 'health' },
};

const PAGE_SIZE_OPTIONS = [25, 50, 100];

function emptyDraft(section: StatusInventorySectionId): Draft {
  if (section === 'entities') {
    return {};
  }
  if (section === 'hubs') {
    return { slug: '', display_name: '', lat: '', lng: '', external_ref: '', metadata: '' };
  }
  if (section === 'models') {
    return {
      slug: '',
      display_name: '',
      vendor: '',
      model_match: '',
      default_monitoring_enabled: true,
      metadata: '',
    };
  }
  if (section === 'devices') {
    return {
      discovery_enabled: true,
      hostname: '',
      display_name: '',
      site_id: '',
      device_type_id: '',
      management_address: '',
      device_role: '',
      snmp_auth: '',
      snmp_community: '',
      snmp_version: '2c',
      snmp_port: '161',
      snmp_walk_params: '',
      monitoring_enabled: true,
      metadata: '',
    };
  }
  if (section === 'overlays') {
    return {
      device_type_id: '',
      overlay_name: '',
      module_name: '',
      overlay_config: '{\n  "walk": [],\n  "metrics": []\n}',
      version: '1',
      active: true,
    };
  }
  if (section === 'auth') {
    return {
      auth_name: '',
      display_name: '',
      snmp_version: '2',
      community: '',
      username: '',
      security_level: '',
      auth_protocol: '',
      auth_password: '',
      priv_protocol: '',
      priv_password: '',
      active: true,
      metadata: '',
    };
  }
  return {
    slug: '',
    display_name: '',
    link_kind: '',
    capacity_bps: '',
    monitoring_enabled: true,
    source_site_id: '',
    source_device_id: '',
    source_interface_id: '',
    target_site_id: '',
    target_device_id: '',
    target_interface_id: '',
    metadata: '',
  };
}

function deviceDraft(device: StatusDevice): Draft {
  return {
    hostname: device.hostname,
    display_name: device.display_name ?? '',
    site_id: device.site_id,
    device_type_id: device.device_type_id ?? '',
    external_ref: device.external_ref ?? '',
    management_address: device.management_address ?? '',
    device_role: device.device_role ?? '',
    vendor: device.vendor ?? '',
    model: device.model ?? '',
    monitoring_enabled: device.monitoring_enabled,
    metadata: JSON.stringify(device.metadata ?? {}, null, 2),
    discovery_enabled: false,
    snmp_auth: String(device.metadata?.snmp_auth ?? ''),
    snmp_walk_params: String(device.metadata?.snmp_walk_params ?? ''),
  };
}

export function useStatusInventoryController(): StatusInventoryViewProps {
  const { activeTenant } = useScope();
  const access = useStatusInventoryAccess();
  const { path, navigate } = useNavigation();
  const activeSectionId = parseSectionFromPath(path);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [detailPage, setDetailPage] = useState(0);
  const [detailSearch, setDetailSearch] = useState('');
  const [deviceDetailTabId, setDeviceDetailTabId] = useState('interfaces');
  const [selectedDeviceInterfaceId, setSelectedDeviceInterfaceId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(() => emptyDraft('hubs'));
  const [validation, setValidation] = useState<Record<string, string>>({});
  const [deviceDiscoveryResult, setDeviceDiscoveryResult] = useState<StatusDeviceDiscoveryResult | null>(null);
  const [selectorSearch, setSelectorSearch] = useState<Record<string, string>>({});
  const [debouncedSelectorSearch, setDebouncedSelectorSearch] = useState<Record<string, string>>({});
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSelectorSearch(selectorSearch), 250);
    return () => window.clearTimeout(timer);
  }, [selectorSearch]);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() =>
    activeSectionId === 'links' ? [{ id: 'lifecycle', value: 'active' }] : []
  );
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [takeoverMode, setTakeoverMode] = useState<TakeoverMode>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationKind, setConfirmationKind] = useState<'delete' | 'discard-changes'>('delete');
  const [confirmationError, setConfirmationError] = useState<string | null>(null);
  const [editorDirty, setEditorDirty] = useState(false);

  const canLoad = Boolean(activeTenant?.id && access.accessEnabled);



  const effectiveColumnFilters = useMemo<ColumnFiltersState>(() => {
    if (activeSectionId !== 'links' || columnFilters.some(filter => filter.id === 'lifecycle')) {
      return columnFilters;
    }
    return [...columnFilters, { id: 'lifecycle', value: 'active' }];
  }, [activeSectionId, columnFilters]);

  const listOptions = useMemo<StatusConfigListOptions>(() => ({
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    q: debouncedSearchValue || undefined,
    sort: sorting.length
      ? sorting.map(item => `${item.desc ? '-' : ''}${item.id}`).join(',')
      : undefined,
    filters: Object.fromEntries(effectiveColumnFilters.map(filter => [
      filter.id,
      Array.isArray(filter.value) ? filter.value.join(',') : String(filter.value ?? ''),
    ])),
  }), [debouncedSearchValue, effectiveColumnFilters, pagination.pageIndex, pagination.pageSize, sorting]);

  const listKey = canLoad
    ? ['status-inventory-list', activeTenant!.id, SECTION_LIST_RESOURCE[activeSectionId], listOptions] as const
    : null;
  const {
    data: activePage,
    error: activePageError,
    isLoading: activePageLoading,
    isValidating: activePageRefreshing,
    mutate: mutateActivePage,
  } = useSWR(listKey, ([, , resource, options]) => listStatusConfigPage<unknown>(resource, options), { refreshInterval: editorDirty ? 0 : 30_000, refreshWhenHidden: false });

  const {
    data: managedServiceSummary,
    error: managedServiceFailure,
    mutate: mutateManagedService,
  } = useSWR(
    canLoad ? ['status-inventory-managed-service', activeTenant!.id] as const : null,
    () => getStatusManagedServiceSummary(),
    { refreshInterval: 30_000, refreshWhenHidden: false },
  );

  const editorNeedsSites = takeoverMode === 'editor' && ['devices', 'links'].includes(activeSectionId);
  const editorNeedsModels = takeoverMode === 'editor' && ['devices', 'overlays'].includes(activeSectionId);
  const editorNeedsAuth = takeoverMode === 'editor' && activeSectionId === 'devices';
  const selectedDeviceId = activeSectionId === 'devices' && selectedRowId ? selectedRowId : null;

  const useSelectorPage = <T,>(resource: StatusConfigResource, enabled: boolean, filters?: StatusConfigListOptions['filters'], searchKey = resource as string, pageIndex = 0) =>
    useSWR(
      canLoad && enabled
        ? ['status-inventory-selector', activeTenant!.id, resource, { page: pageIndex + 1, pageSize: searchKey.startsWith('detail-') ? 50 : 100, filters, q: debouncedSelectorSearch[searchKey] || undefined }] as const
        : null,
      ([, , nextResource, options]) => listStatusConfigPage<T>(nextResource, options),
    );

  // Editor and detail dependencies stay lazy so initial navigation requests only the active page.
  const sitesPage = useSelectorPage<StatusSite>('sites', editorNeedsSites);
  const deviceTypesPage = useSelectorPage<StatusDeviceType>('device-types', editorNeedsModels);
  const authProfilesPage = useSelectorPage<StatusSNMPAuthProfile>('snmp-auth-profiles', editorNeedsAuth);
  const sourceDevicesPage = useSelectorPage<StatusDevice>(
    'devices',
    takeoverMode === 'editor' && activeSectionId === 'links' && Boolean(draft.source_site_id),
    { site_id: String(draft.source_site_id || '') }, 'source_device_id',
  );
  const targetDevicesPage = useSelectorPage<StatusDevice>(
    'devices',
    takeoverMode === 'editor' && activeSectionId === 'links' && Boolean(draft.target_site_id),
    { site_id: String(draft.target_site_id || '') }, 'target_device_id',
  );
  const sourceInterfacesPage = useSelectorPage<StatusInterface>(
    'interfaces',
    takeoverMode === 'editor' && activeSectionId === 'links' && Boolean(draft.source_device_id),
    { device_id: String(draft.source_device_id || '') }, 'source_interface_id',
  );
  const targetInterfacesPage = useSelectorPage<StatusInterface>(
    'interfaces',
    takeoverMode === 'editor' && activeSectionId === 'links' && Boolean(draft.target_device_id),
    { device_id: String(draft.target_device_id || '') }, 'target_interface_id',
  );
  const detailInterfacesPage = useSelectorPage<StatusInterface>(
    'interfaces',
    Boolean(selectedDeviceId),
    { device_id: selectedDeviceId ?? '' }, 'detail-records', detailPage,
  );
  const detailComponentsPage = useSelectorPage<StatusDeviceComponent>(
    'device-components',
    Boolean(selectedDeviceId),
    { device_id: selectedDeviceId ?? '' }, 'detail-records', detailPage,
  );

  const activeItems = activePage?.items ?? [];
  const sites = activeSectionId === 'hubs'
    ? activeItems as StatusSite[]
    : sitesPage.data?.items ?? [];
  const deviceTypes = activeSectionId === 'models'
    ? activeItems as StatusDeviceType[]
    : deviceTypesPage.data?.items ?? [];
  const devices = activeSectionId === 'devices'
    ? activeItems as StatusDevice[]
    : [...(sourceDevicesPage.data?.items ?? []), ...(targetDevicesPage.data?.items ?? [])];
  const interfaces = activeSectionId === 'devices'
    ? detailInterfacesPage.data?.items ?? []
    : [...(sourceInterfacesPage.data?.items ?? []), ...(targetInterfacesPage.data?.items ?? [])];
  const deviceComponents = activeSectionId === 'entities'
    ? activeItems as StatusDeviceComponent[]
    : detailComponentsPage.data?.items ?? [];
  const overlays = activeSectionId === 'overlays'
    ? activeItems as StatusDeviceTypeSNMPOverlay[]
    : [];
  const authProfiles = activeSectionId === 'auth'
    ? activeItems as StatusSNMPAuthProfile[]
    : authProfilesPage.data?.items ?? [];
  const links = activeSectionId === 'links' ? activeItems as StatusLinkConfig[] : [];

  const loadInventory = useCallback(async () => {
    setError(null);
    await mutateActivePage();
  }, [mutateActivePage]);

  useEffect(() => {
    setDetailPage(0);
    setDetailSearch('');
    setSelectorSearch({});
    setDebouncedSelectorSearch({});
    setSelectedRowId(null);
    setDeviceDetailTabId('interfaces');
    setSelectedDeviceInterfaceId(null);
    setDraft(emptyDraft(activeSectionId));
    setValidation({});
    setDeviceDiscoveryResult(null);
    setTakeoverMode(null);
    setEditorDirty(false);
    setSearchValue('');
    setDebouncedSearchValue('');
    const deviceId = new URLSearchParams(path.split('?')[1] ?? '').get('device_id');
    setColumnFilters(activeSectionId === 'links' ? [{ id: 'lifecycle', value: 'active' }] : activeSectionId === 'devices' && deviceId ? [{ id: 'device_id', value: deviceId }] : []);
    setSorting([]);
    setPagination({ pageIndex: 0, pageSize: 50 });
  }, [activeSectionId, activeTenant?.id]);



  const writeLocation = useCallback((patch: { q?: string; page?: number; size?: number; sort?: SortingState; filters?: ColumnFiltersState; inspect?: string | null }) => {
    const params = new URLSearchParams();
    const q = patch.q ?? searchValue;
    if (q) params.set('q', q);
    const nextPage = patch.page ?? pagination.pageIndex;
    if (nextPage) params.set('page', String(nextPage + 1));
    const size = patch.size ?? pagination.pageSize;
    if (size !== 50) params.set('size', String(size));
    const order = patch.sort ?? sorting;
    if (order.length) params.set('sort', order.map(item => `${item.desc ? '-' : ''}${item.id}`).join(','));
    for (const item of patch.filters ?? columnFilters) params.set(`filter[${item.id}]`, String(item.value));
    const inspect = patch.inspect === undefined ? selectedRowId : patch.inspect;
    if (inspect) params.set('inspect', inspect);
    const query = params.toString();
    navigate(`/status/inventory/${activeSectionId}${query ? `?${query}` : ''}`, { replace: true });
  }, [searchValue, pagination.pageIndex, pagination.pageSize, sorting, columnFilters, selectedRowId, activeSectionId, navigate]);
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearchValue(searchValue.trim());
      const current = new URLSearchParams(path.split('?')[1] ?? '').get('q') ?? '';
      if (current !== searchValue.trim()) writeLocation({ q: searchValue.trim(), page: 0 });
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [searchValue, path, writeLocation]);
  useEffect(() => {
    const params = new URLSearchParams(path.split('?')[1] ?? '');
    const filters: ColumnFiltersState = [];
    params.forEach((value, key) => { const match = /^filter\[([a-z_]+)\]$/.exec(key); if (match) filters.push({ id: match[1], value }); });
    if (activeSectionId === 'devices' && params.get('device_id')) filters.push({ id: 'device_id', value: params.get('device_id') });
    if (activeSectionId === 'links' && !filters.some(item => item.id === 'lifecycle')) filters.push({ id: 'lifecycle', value: 'active' });
    setSearchValue(params.get('q') ?? '');
    setDebouncedSearchValue(params.get('q') ?? '');
    setColumnFilters(filters);
    setSorting((params.get('sort') ?? '').split(',').filter(Boolean).map(value => ({ id: value.replace(/^-/, ''), desc: value.startsWith('-') })));
    const size = Number(params.get('size') ?? 50);
    const page = Number(params.get('page') ?? 1);
    setPagination({ pageIndex: Number.isSafeInteger(page) && page > 0 ? page - 1 : 0, pageSize: PAGE_SIZE_OPTIONS.includes(size) ? size : 50 });
  }, [path, activeSectionId]);
  const quickViews = [
    { id: 'all', label: 'All devices', filters: [] },
    { id: 'attention', label: 'Needs attention', filters: [{ id: 'collection_state', value: 'attention' }] },
    { id: 'missing', label: 'Missing telemetry', filters: [{ id: 'missing_telemetry', value: 'true' }] },
    { id: 'unconfigured', label: 'Unconfigured', filters: [{ id: 'readiness', value: 'no_overlay' }] },
    { id: 'disabled', label: 'Monitoring disabled', filters: [{ id: 'monitoring_enabled', value: 'false' }] },
  ];

  const selectedDeviceForDetail = activeSectionId === 'devices' && selectedRowId
    ? devices.find(device => device.device_id === selectedRowId) ?? null
    : null;

  const sections = useMemo<ControllerSection[]>(() => [
    {
      id: 'hubs',
      label: 'Hubs',
      description: 'Canonical status sites used as link endpoints and map anchors.',
      createLabel: 'Add hub',
      emptyTitle: 'No hubs configured',
      emptyDescription: 'Create the first hub before assigning devices or links.',
      columns: [
        { id: 'slug', label: 'Slug' },
        { id: 'location', label: 'Location' },
        { id: 'external', label: 'External ref' },
      ],
      rows: sites.map(site => ({
        id: site.site_id,
        title: site.display_name,
        subtitle: site.slug,
        cells: {
          slug: site.slug,
          location: `${site.location?.lat ?? 0}, ${site.location?.lng ?? 0}`,
          external: site.external_ref ?? '',
        },
      })),
    },
    {
      id: 'models',
      label: 'Device models',
      description: 'Tenant-supported device model catalog used by discovery and device assignment.',
      createLabel: 'Add model',
      emptyTitle: 'No device models configured',
      emptyDescription: 'Add supported models before assigning device types or SNMP overlays.',
      columns: [
        { id: 'vendor', label: 'Vendor' },
        { id: 'match', label: 'Model match' },
        { id: 'overlays', label: 'Active overlays' },
      ],
      rows: deviceTypes.map(type => {
        const activeOverlayCount = type.active_overlay_count ?? 0;
        return {
          id: type.device_type_id,
          title: type.display_name,
          subtitle: type.slug,
          badges: [
            { id: 'scope', label: type.read_only ? 'System catalog' : 'Tenant model', tone: type.read_only ? 'info' : 'neutral' },
            { id: 'monitoring-default', label: type.default_monitoring_enabled ? 'Monitoring default on' : 'Monitoring default off', tone: type.default_monitoring_enabled ? 'success' : 'warning' },
          ],
          cells: {
            vendor: type.vendor ?? '',
            match: type.model_match ?? '',
            overlays: activeOverlayCount ? String(activeOverlayCount) : 'None active',
          },
        };
      }),
    },
    {
      id: 'devices',
      label: 'Devices',
      description: 'Manage devices, monitoring and collection readiness.',
      createLabel: 'Add device',
      emptyTitle: 'No devices configured',
      emptyDescription: 'Enter a hostname and SNMP credentials to discover a supported device model.',
      columns: [
        { id: 'site', label: 'Hub' },
        { id: 'type', label: 'Device type' },
        { id: 'address', label: 'Management address' },
        { id: 'collection', label: 'Collection' },
        { id: 'last_success', label: 'Last success' },
      ],
      rows: devices.map(device => {
        const type = deviceTypes.find(item => item.device_type_id === device.device_type_id);
        const activeOverlayCount = device.active_overlay_count ?? 0;
        return {
          id: device.device_id,
          title: device.display_name || device.hostname,
          subtitle: device.hostname,
          badges: [
            { id: 'monitoring', label: device.monitoring_enabled ? 'Monitoring on' : 'Monitoring off', tone: 'neutral' },
            { id: 'overlay-ready', label: activeOverlayCount > 0 ? `${activeOverlayCount} active overlay${activeOverlayCount === 1 ? '' : 's'}` : 'No active overlay', tone: activeOverlayCount > 0 ? 'success' : 'danger' },
          ],
          cells: {
            site: device.site_display_name ?? sites.find(site => site.site_id === device.site_id)?.display_name ?? 'Unassigned',
            type: device.device_type_display_name || type?.display_name || 'Unassigned',
            address: device.management_address ?? '',
            collection: (device.collection_state ?? 'unknown').replace(/_/g, ' '),
            last_success: device.last_success_at ? new Date(device.last_success_at).toLocaleString() : 'No successful sample',
          },
        };
      }),
      detail: selectedDeviceForDetail ? buildDeviceDetail(selectedDeviceForDetail, interfaces, deviceComponents, deviceDetailTabId, selectedDeviceInterfaceId) : null,
    },
    {
      id: 'auth',
      label: 'SNMP credentials',
      description: 'Tenant-managed SNMP credential profiles referenced by monitored devices.',
      createLabel: 'Add auth profile',
      emptyTitle: 'No SNMP auth profiles configured',
      emptyDescription: 'Create at least one SNMP auth profile before enabling Alloy scraping.',
      columns: [
        { id: 'version', label: 'Version' },
        { id: 'secret', label: 'Secret status' },
        { id: 'active', label: 'Active' },
      ],
      rows: authProfiles.map(profile => ({
        id: profile.snmp_auth_profile_id,
        title: profile.display_name,
        subtitle: profile.auth_name,
        badges: [
          { id: 'active', label: profile.active ? 'Active' : 'Inactive', tone: profile.active ? 'success' : 'warning' },
        ],
        cells: {
          version: `SNMP v${profile.snmp_version}`,
          secret: profile.snmp_version === 3 ? (profile.has_auth_password || profile.has_priv_password ? 'Secrets set' : 'Missing secrets') : (profile.has_community ? 'Community set' : 'Missing community'),
          active: profile.active ? 'Yes' : 'No',
        },
      })),
    },
    {
      id: 'entities',
      label: 'Hardware',
      description: 'Read-only components and sensors materialized from Alloy SNMP overlay samples.',
      createLabel: null,
      emptyTitle: 'No entities discovered',
      emptyDescription: 'Entities appear after Alloy scrapes a monitored device and svc-status materializes component or sensor samples.',
      columns: [
        { id: 'device', label: 'Device' },
        { id: 'kind', label: 'Kind' },
        { id: 'sensor', label: 'Sensor' },
        { id: 'value', label: 'Current value' },
        { id: 'status', label: 'Sensor status' },
      ],
      rows: deviceComponents.map(component => {
        const device = devices.find(item => item.device_id === component.device_id);
        const iface = component.interface_id ? interfaces.find(item => item.interface_id === component.interface_id) : null;
        const hasSensor = isSensorComponent(component);
        return {
          id: component.component_id,
          title: component.name || component.component_key,
          subtitle: [component.component_key, iface?.name].filter(Boolean).join(' / '),
          badges: [
            { id: 'monitoring', label: component.monitoring_enabled ? 'Monitoring on' : 'Monitoring off', tone: component.monitoring_enabled ? 'success' : 'warning' },
            ...(hasSensor ? [{ id: 'sensor', label: 'Sensor', tone: 'info' as const }] : []),
          ],
          cells: {
            device: device?.display_name || device?.hostname || component.device_id,
            kind: component.component_kind || 'Component',
            sensor: component.sensor_type || 'Not a sensor',
            value: formatSensorValue(component),
            status: formatSensorStatus(component.sensor_oper_status),
          },
        };
      }),
    },
    {
      id: 'overlays',
      label: 'Collection profiles',
      description: 'Alloy SNMP module overlays attached to supported device models.',
      createLabel: 'Add overlay',
      emptyTitle: 'No SNMP overlays configured',
      emptyDescription: 'Add at least one active overlay for each supported model that should be scraped.',
      columns: [
        { id: 'model', label: 'Device model' },
        { id: 'module', label: 'Module' },
        { id: 'version', label: 'Version' },
      ],
      rows: overlays.map(overlay => {
        const type = deviceTypes.find(item => item.device_type_id === overlay.device_type_id);
        return {
          id: overlay.device_type_snmp_overlay_id,
          title: overlay.overlay_name,
          subtitle: type?.display_name ?? 'Unknown model',
          badges: [
            { id: 'scope', label: overlay.read_only ? 'System overlay' : 'Tenant overlay', tone: overlay.read_only ? 'info' : 'neutral' },
            { id: 'active', label: overlay.active ? 'Active' : 'Inactive', tone: overlay.active ? 'success' : 'warning' },
          ],
          cells: {
            model: type?.display_name ?? '',
            module: overlay.module_name,
            version: String(overlay.version ?? 1),
          },
        };
      }),
    },
    {
      id: 'links',
      label: 'Links',
      description: 'Tenant-scoped monitored links with source and target endpoints.',
      createLabel: 'Add link',
      emptyTitle: 'No links configured',
      emptyDescription: 'Create links after hubs and endpoint devices are available.',
      columns: [
        { id: 'source', label: 'Source' },
        { id: 'target', label: 'Target' },
        { id: 'state', label: 'Current state' },
      ],
      rows: links.map(link => {
        return {
          id: link.link_id,
          title: link.display_name,
          subtitle: link.slug,
          badges: [{ id: 'monitoring', label: link.monitoring_enabled ? 'Monitoring on' : 'Monitoring off', tone: link.monitoring_enabled ? 'success' : 'warning' }],
          cells: {
            source: endpointLabel(link.source, sites, devices, interfaces),
            target: endpointLabel(link.target, sites, devices, interfaces),
            state: link.health ?? link.current_health ?? 'Unknown',
          },
        };
      }),
    },
  ], [authProfiles, deviceComponents, deviceDetailTabId, deviceTypes, devices, interfaces, links, overlays, selectedDeviceForDetail, selectedDeviceInterfaceId, sites]);

  const openDeviceDetail = useCallback((device: StatusDevice) => {
    setSelectedRowId(device.device_id);
    setDraft(deviceDraft(device));
    setValidation({});
    setDeviceDiscoveryResult(null);
    setDeviceDetailTabId('interfaces');
    setDetailPage(0);
    setDetailSearch('');
    setSelectedDeviceInterfaceId(null);
    setTakeoverMode('device-detail');
  }, []);

  useEffect(() => {
    const id = new URLSearchParams(path.split('?')[1] ?? '').get('inspect');
    if (!id || activeSectionId !== 'devices' || selectedRowId === id || editorDirty) return;
    const device = devices.find(item => item.device_id === id);
    if (device) openDeviceDetail(device);
  }, [path, activeSectionId, activePage, selectedRowId, editorDirty, openDeviceDetail]);

  const selectedRowReadOnly = useMemo(() => {
    if (!selectedRowId) {
      return false;
    }
    if (activeSectionId === 'models') {
      return Boolean(deviceTypes.find(item => item.device_type_id === selectedRowId)?.read_only);
    }
    if (activeSectionId === 'overlays') {
      return Boolean(overlays.find(item => item.device_type_snmp_overlay_id === selectedRowId)?.read_only);
    }
    if (activeSectionId === 'entities') {
      return true;
    }
    return false;
  }, [activeSectionId, deviceTypes, overlays, selectedRowId]);

  const baseEditorFields = useMemo(
    () => buildEditorFields(activeSectionId, draft, validation, sites, deviceTypes, devices, interfaces, authProfiles, selectedRowReadOnly, deviceDiscoveryResult),
    [activeSectionId, authProfiles, deviceDiscoveryResult, deviceTypes, devices, draft, interfaces, selectedRowReadOnly, sites, validation]
  );

  const selectorPages: Record<string, typeof sitesPage | typeof deviceTypesPage | typeof authProfilesPage | typeof sourceDevicesPage | typeof sourceInterfacesPage> = {
    site_id: sitesPage, source_site_id: sitesPage, target_site_id: sitesPage,
    device_type_id: deviceTypesPage, snmp_auth: authProfilesPage,
    source_device_id: sourceDevicesPage, target_device_id: targetDevicesPage,
    source_interface_id: sourceInterfacesPage, target_interface_id: targetInterfacesPage,
  };
  const selectorKey = (id: string) => ['site_id', 'source_site_id', 'target_site_id'].includes(id) ? 'sites'
    : id === 'device_type_id' ? 'device-types' : id === 'snmp_auth' ? 'snmp-auth-profiles' : id;
  const editorFields = baseEditorFields.map(field => {
    const page = selectorPages[field.id];
    if (!page) return field;
    return { ...field, kind: 'async-select' as const, optionsSearchValue: selectorSearch[selectorKey(field.id)] ?? '',
      optionsLoading: page.isLoading, optionsError: page.error ? 'Unable to load options.' : undefined,
      optionsEmptyLabel: 'No matches. Search by name or address.',
      description: 'Showing up to 100 matches. Search to find any record.' };
  });

  const handleCreate = useCallback((section: StatusInventorySectionId) => {
    if (section === 'entities') {
      void loadInventory();
      return;
    }
    navigate(`/status/inventory/${section}`);
    setSelectedRowId(null);
    setDraft(emptyDraft(section));
    setValidation({});
    setDeviceDiscoveryResult(null);
    setTakeoverMode('editor');
    setEditorDirty(false);
  }, [loadInventory, navigate]);

  const handleSelectRow = useCallback((rowId: string) => {
    if (activeSectionId === 'devices') {
      const device = devices.find(item => item.device_id === rowId);
      if (device) {
        openDeviceDetail(device);
      }
      return;
    }

    setSelectedRowId(rowId);
    setValidation({});
    setDeviceDiscoveryResult(null);
    setEditorDirty(false);

    if (activeSectionId !== 'entities') {
      setTakeoverMode('editor');
    }

    if (activeSectionId === 'hubs') {
      const site = sites.find(item => item.site_id === rowId);
      if (site) {
        setDraft({
          slug: site.slug,
          display_name: site.display_name,
          lat: String(site.location?.lat ?? ''),
          lng: String(site.location?.lng ?? ''),
          external_ref: site.external_ref ?? '',
          metadata: JSON.stringify(site.metadata ?? {}, null, 2),
        });
      }
    } else if (activeSectionId === 'models') {
      const type = deviceTypes.find(item => item.device_type_id === rowId);
      if (type) {
        setDraft({
          slug: type.slug,
          display_name: type.display_name,
          vendor: type.vendor ?? '',
          model_match: type.model_match ?? '',
          default_monitoring_enabled: type.default_monitoring_enabled,
          metadata: JSON.stringify(type.metadata ?? {}, null, 2),
        });
      }
    } else if (activeSectionId === 'auth') {
      const profile = authProfiles.find(item => item.snmp_auth_profile_id === rowId);
      if (profile) {
        setDraft({
          auth_name: profile.auth_name,
          display_name: profile.display_name,
          snmp_version: String(profile.snmp_version ?? 2),
          community: '',
          username: profile.username ?? '',
          security_level: profile.security_level ?? '',
          auth_protocol: profile.auth_protocol ?? '',
          auth_password: '',
          priv_protocol: profile.priv_protocol ?? '',
          priv_password: '',
          active: profile.active,
          metadata: JSON.stringify(profile.metadata ?? {}, null, 2),
        });
      }
    } else if (activeSectionId === 'entities') {
      setDraft({});
    } else if (activeSectionId === 'overlays') {
      const overlay = overlays.find(item => item.device_type_snmp_overlay_id === rowId);
      if (overlay) {
        setDraft({
          device_type_id: overlay.device_type_id,
          overlay_name: overlay.overlay_name,
          module_name: overlay.module_name,
          overlay_config: JSON.stringify(overlay.overlay_config ?? {}, null, 2),
          version: String(overlay.version ?? 1),
          active: overlay.active,
        });
      }
    } else {
      const link = links.find(item => item.link_id === rowId);
      if (link) {
        setDraft({
          slug: link.slug,
          display_name: link.display_name,
          link_kind: link.link_kind ?? '',
          capacity_bps: link.capacity_bps ? String(link.capacity_bps) : '',
          monitoring_enabled: link.monitoring_enabled,
          source_site_id: link.source.site_id,
          source_device_id: link.source.device_id ?? '',
          source_interface_id: link.source.interface_id ?? '',
          target_site_id: link.target.site_id,
          target_device_id: link.target.device_id ?? '',
          target_interface_id: link.target.interface_id ?? '',
          metadata: '{}',
        });
      }
    }
  }, [activeSectionId, authProfiles, deviceTypes, devices, links, openDeviceDetail, overlays, sites]);

  const handleDetailTabChange = useCallback((sectionId: StatusInventorySectionId, tabId: string) => {
    if (sectionId !== 'devices') {
      return;
    }
    setDeviceDetailTabId(tabId);
    setDetailPage(0);
  }, []);

  const handleDetailRowSelect = useCallback((sectionId: StatusInventorySectionId, tabId: string, rowId: string) => {
    if (sectionId !== 'devices') {
      return;
    }
    if (tabId === 'interfaces') {
      setSelectedDeviceInterfaceId(rowId);
      setDeviceDetailTabId('interface-entities');
    }
  }, []);

  const handleFieldChange = useCallback((fieldId: string, value: string | boolean) => {
    setEditorDirty(true);
    setDraft(current => {
      const next = { ...current, [fieldId]: value };
      if (fieldId === 'source_site_id') {
        next.source_device_id = '';
        next.source_interface_id = '';
      }
      if (fieldId === 'target_site_id') {
        next.target_device_id = '';
        next.target_interface_id = '';
      }
      if (fieldId === 'source_device_id') {
        next.source_interface_id = '';
      }
      if (fieldId === 'target_device_id') {
        next.target_interface_id = '';
      }
      return next;
    });
    if (['hostname', 'management_address', 'snmp_community', 'snmp_version', 'snmp_port', 'discovery_enabled'].includes(fieldId)) {
      setDeviceDiscoveryResult(null);
    }
    setValidation(current => ({ ...current, [fieldId]: '' }));
  }, []);

  const handleSave = useCallback(async () => {
    if (activeSectionId === 'entities') {
      setError('Discovered entities are read-only and are updated by SNMP materialization.');
      return;
    }
    if (selectedRowReadOnly) {
      setError('System-managed catalog records are read-only.');
      return;
    }
    const useDiscovery = activeSectionId === 'devices' && !selectedRowId && Boolean(draft.discovery_enabled);
    if (useDiscovery && !deviceDiscoveryResult) {
      const localValidation = validateDiscoveryProbeDraft(draft, authProfiles);
      if (Object.keys(localValidation).length > 0) {
        setValidation(localValidation);
        return;
      }
      setSaving(true);
      setValidation({});
      setError(null);
      try {
        const result = await discoverStatusDevice(buildDiscoveryPayload(draft, { previewOnly: true }));
        setDeviceDiscoveryResult(result);
        setDraft(current => ({
          ...current,
          device_type_id: result.device_type?.device_type_id ?? '',
        }));
        toast.success(result.matched && result.device_type ? `Discovered ${result.device_type.display_name}.` : 'Discovery completed. Select a custom model before creating the device.');
      } catch (err) {
        if (err instanceof StatusInventoryError && err.issues.length) {
          setValidation(Object.fromEntries(err.issues.map(issue => [fieldKey(issue.field), issue.message])));
        } else {
          setError(err instanceof Error ? err.message : 'Failed to run device discovery.');
        }
      } finally {
        setSaving(false);
      }
      return;
    }
    const localValidation = useDiscovery
      ? validateDiscoverySubmitDraft(draft, deviceDiscoveryResult, authProfiles)
      : validateDraft(activeSectionId, draft, authProfiles);
    if (Object.keys(localValidation).length > 0) {
      setValidation(localValidation);
      return;
    }
    setSaving(true);
    setValidation({});
    try {
      const resource = SECTION_RESOURCE[activeSectionId];
      if (useDiscovery) {
        const result = await discoverStatusDevice(buildDiscoveryPayload(draft, { identity: deviceDiscoveryResult?.identity }));
        toast.success(`Created ${result.device?.display_name || result.device?.hostname || draft.hostname}.`);
      } else if (selectedRowId) {
        await updateStatusConfig(resource, selectedRowId, buildPayload(activeSectionId, draft));
      } else {
        await createStatusConfig(resource, buildPayload(activeSectionId, draft));
      }
      if (!useDiscovery) {
        toast.success(selectedRowId ? 'Status inventory updated.' : 'Status inventory created.');
      }
      await loadInventory();
      setSelectedRowId(null);
      setDraft(emptyDraft(activeSectionId));
      setDeviceDiscoveryResult(null);
      setTakeoverMode(null);
      setEditorDirty(false);
    } catch (err) {
      if (err instanceof StatusInventoryError && err.issues.length) {
        setValidation(Object.fromEntries(err.issues.map(issue => [fieldKey(issue.field), issue.message])));
      } else {
        setError(err instanceof Error ? err.message : 'Failed to save status inventory.');
      }
    } finally {
      setSaving(false);
    }
  }, [activeSectionId, authProfiles, deviceDiscoveryResult, draft, loadInventory, selectedRowId, selectedRowReadOnly]);

  const handleDelete = useCallback(async () => {
    if (!selectedRowId) {
      return;
    }
    if (activeSectionId === 'entities') {
      setError('Discovered entities are read-only and are updated by SNMP materialization.');
      return;
    }
    if (selectedRowReadOnly) {
      setError('System-managed catalog records are read-only.');
      return;
    }
    setDeletingId(selectedRowId);
    setConfirmationError(null);
    try {
      const selectedLink = activeSectionId === 'links'
        ? links.find(link => link.link_id === selectedRowId)
        : undefined;
      if (selectedLink?.discovery_managed) {
        await suppressStatusLink(selectedRowId);
        toast.success('Link removed from active topology. It can be restored from Suppressed Links.');
      } else {
        await deleteStatusConfig(SECTION_RESOURCE[activeSectionId], selectedRowId);
        toast.success('Status inventory deleted.');
      }
      await loadInventory();
      setSelectedRowId(null);
      setDraft(emptyDraft(activeSectionId));
      setDeviceDiscoveryResult(null);
      setTakeoverMode(null);
      setEditorDirty(false);
      setConfirmationOpen(false);
    } catch (err) {
      if (err instanceof StatusInventoryError && err.issues.length) {
        setValidation(Object.fromEntries(err.issues.map(issue => [fieldKey(issue.field), issue.message])));
      } else {
        const message = err instanceof Error ? err.message : 'Failed to delete status inventory.';
        setError(message);
        setConfirmationError(message);
      }
    } finally {
      setDeletingId(null);
    }
  }, [activeSectionId, links, loadInventory, selectedRowId, selectedRowReadOnly]);

  const handleActivateRow = useCallback(async (rowId: string) => {
    if (activeSectionId === 'links') {
      const link = links.find(item => item.link_id === rowId);
      if (link && isLinkSuppressed(link)) {
        setSaving(true);
        setError(null);
        try {
          await restoreStatusLink(rowId);
          toast.success('Link restored. Discovery may rematerialize current state shortly.');
          await loadInventory();
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to restore the link.');
        } finally {
          setSaving(false);
        }
        return;
      }
    }
    handleSelectRow(rowId);
  }, [activeSectionId, handleSelectRow, links, loadInventory]);

  const activeSection = sections.find(s => s.id === activeSectionId);
  const editorOpen = takeoverMode === 'editor' && activeSectionId !== 'entities';
  const editorTitle = selectedRowId ? `Edit ${activeSection?.label ?? 'Inventory'}` : activeSection?.createLabel;
  const editorDescription = activeSection?.description ?? 'Manage tenant-scoped status inventory.';
  const useDiscoveryAction = activeSectionId === 'devices' && !selectedRowId && Boolean(draft.discovery_enabled) && !deviceDiscoveryResult;
  const saveLabel = useDiscoveryAction
    ? 'Run discovery'
    : selectedRowId
      ? 'Save changes'
      : activeSection?.createLabel ?? 'Create';
  const deleteAllowed = Boolean(selectedRowId && !selectedRowReadOnly && activeSectionId !== 'entities');

  const handleCancelEdit = useCallback(() => {
    const params = new URLSearchParams(path.split('?')[1] ?? '');
    params.delete('inspect');
    navigate(`/status/inventory/${activeSectionId}${params.size ? `?${params}` : ''}`, { replace: true });
    setSelectedRowId(null);
    setDraft(emptyDraft(activeSectionId));
    setValidation({});
    setDeviceDiscoveryResult(null);
    setTakeoverMode(null);
    setEditorDirty(false);
  }, [activeSectionId, path, navigate]);

  const accessState = !activeTenant?.id
    ? { kind: 'missing-tenant' as const, title: 'Select a tenant', description: 'Status inventory requires an active tenant context.' }
    : !access.statusFeatureEnabled
      ? { kind: 'feature-unavailable' as const, title: 'Status inventory is not enabled', description: 'Enable the status inventory tenant feature before managing inventory.' }
      : !access.statusPermissionGranted
        ? { kind: 'permission-denied' as const, title: 'Permission required', description: 'Your current role does not grant status inventory management for this tenant.' }
        : { kind: 'ready' as const };

  const activeRows = (activeSection?.rows ?? []).map(row => {
    const link = activeSectionId === 'links' ? links.find(item => item.link_id === row.id) : undefined;
    return {
      ...row,
      actionLabel: link && isLinkSuppressed(link) ? 'Restore' : activeSectionId === 'devices' ? 'View' : 'View / Edit',
      actionDisabled: activeSectionId === 'entities',
      actionDescription: link && isLinkSuppressed(link)
        ? `Restore ${row.title} to discovery eligibility`
        : `${activeSectionId === 'devices' ? 'View' : 'Edit'} ${row.title}`,
    };
  });
  const activeFilters = effectiveColumnFilters
    .filter(filter => filter.id !== 'lifecycle')
    .map(filter => ({
    id: filter.id,
    label: filter.id,
    value: Array.isArray(filter.value) ? filter.value.join(', ') : String(filter.value ?? ''),
    }));
  const linkLifecycle = activeSectionId === 'links'
    ? String(effectiveColumnFilters.find(filter => filter.id === 'lifecycle')?.value ?? 'active') as 'active' | 'suppressed'
    : undefined;
  const listError = error ?? (activePageError instanceof Error ? activePageError.message : activePageError ? 'Failed to load status inventory.' : undefined);
  const managedServiceError = managedServiceFailure instanceof Error
    ? managedServiceFailure.message
    : managedServiceFailure
      ? 'Failed to load Status Edge service state.'
      : undefined;

  return {
    quickViews: activeSectionId === 'devices' ? quickViews.map(view => ({ ...view, active: JSON.stringify(view.filters) === JSON.stringify(columnFilters) })) : undefined,
    onQuickView: (id: string) => { const view = quickViews.find(item => item.id === id); if (view) { setColumnFilters(view.filters); setPagination(current => ({ ...current, pageIndex: 0 })); writeLocation({ filters: view.filters, page: 0, inspect: null }); } },
    title: activeSection?.label ?? 'Status inventory',
    description: activeSection?.description ?? 'Manage tenant-scoped status topology.',
    eyebrow: 'Status inventory',
    activeSectionId,
    sections: sections.map(section => ({
      id: section.id,
      label: section.label,
      description: section.description,
      createLabel: section.createLabel,
      readOnly: section.id === 'entities',
    })),
    access: accessState,
    list: {
      columns: (activeSection?.columns ?? []).map(column => ({
        ...column,
        sortable: Boolean(SECTION_SORT_FIELDS[activeSectionId][column.id]),
        sortDirection: sorting.find(item => item.id === SECTION_SORT_FIELDS[activeSectionId][column.id])
          ? (sorting.find(item => item.id === SECTION_SORT_FIELDS[activeSectionId][column.id])!.desc ? 'desc' as const : 'asc' as const)
          : null,
        priority: column === activeSection?.columns[0]
          ? 'identity' as const
          : column.id === 'state' || column.id === 'status'
            ? 'status' as const
            : 'detail' as const,
      })),
      rows: activeRows,
      total: activePage?.total ?? activeRows.length,
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      searchValue,
      searchPlaceholder: `Search ${activeSection?.label.toLowerCase() ?? 'inventory'}…`,
      activeFilters,
      lifecycle: linkLifecycle,
      sort: sorting[0] ? { columnId: sorting[0].id, direction: sorting[0].desc ? 'desc' as const : 'asc' as const } : null,
      initialLoading: Boolean(activePageLoading && !activePage),
      refreshing: Boolean(activePageRefreshing && activePage),
      error: listError,
      emptyTitle: activeSection?.emptyTitle ?? 'No records found',
      emptyDescription: activeSection?.emptyDescription ?? 'There is no inventory to display.',
      noResultsTitle: 'No results found',
      noResultsDescription: `No ${activeSection?.label.toLowerCase() ?? 'records'} match the current query.`,
    },
    editor: editorOpen ? {
      open: true,
      title: editorTitle ?? 'Create',
      description: editorDescription,
      fields: editorFields,
      dirty: editorDirty,
      saving,
      deleting: Boolean(deletingId && deletingId === selectedRowId),
      mutationError: error ?? undefined,
      saveLabel,
      deleteLabel: deleteAllowed
        ? activeSectionId === 'links' && links.find(item => item.link_id === selectedRowId)?.discovery_managed
          ? 'Remove from topology'
          : 'Delete'
        : null,
      cancelLabel: 'Cancel',
    } : undefined,
    confirmation: confirmationOpen ? {
      open: true,
      kind: confirmationKind,
      title: confirmationKind === 'discard-changes'
        ? 'Discard unsaved changes?'
        : activeSectionId === 'links'
          ? 'Remove this link from active topology?'
          : `Delete this ${activeSection?.label.toLowerCase() ?? 'record'}?`,
      description: confirmationKind === 'discard-changes'
        ? 'Your unsaved changes will be lost.'
        : activeSectionId === 'links'
          ? 'Discovery-managed links are suppressed reversibly, retaining their history and preventing rediscovery until restored.'
          : 'This action may be blocked when dependent inventory still references the record.',
      confirmLabel: confirmationKind === 'discard-changes'
        ? 'Discard changes'
        : activeSectionId === 'links'
          ? 'Remove link'
          : 'Delete',
      cancelLabel: 'Cancel',
      busy: Boolean(deletingId),
      error: confirmationError ?? undefined,
      destructive: confirmationKind === 'delete',
    } : undefined,
    detail: takeoverMode === 'device-detail' && activeSection?.detail ? { ...activeSection.detail, pageIndex: detailPage, searchValue: detailSearch, total: deviceDetailTabId === 'interfaces' ? detailInterfacesPage.data?.total : detailComponentsPage.data?.total } : undefined,
    notices: managedServiceError
      ? [{ id: 'managed-service-error', title: 'Status Edge readiness unavailable', description: managedServiceError, tone: 'warning' as const }]
      : managedServiceSummary
        ? [{
          id: 'managed-service',
          title: `${managedServiceSummary.service_name}: ${labelizeManagedService(managedServiceSummary.health_state)}`,
          description: `${managedServiceSummary.healthy_module_count} healthy · ${managedServiceSummary.unhealthy_module_count} need attention · ${managedServiceSummary.awaiting_module_count} awaiting samples · ${managedServiceSummary.healthy_module_count + managedServiceSummary.unhealthy_module_count + managedServiceSummary.awaiting_module_count} expected checks`,
          tone: managedServiceSummary.health_state === 'healthy' ? 'success' as const : 'warning' as const,
        }]
        : [],
    onSectionChange: (sectionId: StatusInventorySectionId) => navigate(`/status/inventory/${sectionId}`),
    onSearchChange: (value: string) => { setSearchValue(value); setPagination(current => ({ ...current, pageIndex: 0 })); },
    onClearQuery: () => { setSearchValue(''); writeLocation({ q: '', page: 0 }); },
    onRemoveFilter: (filterId: string) => { const filters = columnFilters.filter(item => item.id !== filterId); setColumnFilters(filters); setPagination(current => ({ ...current, pageIndex: 0 })); writeLocation({ filters, page: 0 }); },
    onClearFilters: () => { const filters = activeSectionId === 'links' ? [{ id: 'lifecycle', value: 'active' }] : []; setColumnFilters(filters); setPagination(current => ({ ...current, pageIndex: 0 })); writeLocation({ filters, page: 0 }); },
    onLifecycleChange: activeSectionId === 'links'
      ? lifecycle => {
        const filters = [...columnFilters.filter(filter => filter.id !== 'lifecycle'), { id: 'lifecycle', value: lifecycle }];
        setColumnFilters(filters);
        writeLocation({ filters, page: 0 });
        setPagination(current => ({ ...current, pageIndex: 0 }));
      }
      : undefined,
    onSortChange: (columnId: string, nextDirection: 'asc' | 'desc' | null) => {
      const sortField = SECTION_SORT_FIELDS[activeSectionId][columnId];
      const sort = nextDirection && sortField ? [{ id: sortField, desc: nextDirection === 'desc' }] : [];
      setSorting(sort);
      writeLocation({ sort, page: 0 });
      setPagination(current => ({ ...current, pageIndex: 0 }));
    },
    onPageChange: (pageIndex: number) => { setPagination(current => ({ ...current, pageIndex })); writeLocation({ page: pageIndex }); },
    onPageSizeChange: (pageSize: number) => { setPagination({ pageIndex: 0, pageSize }); writeLocation({ page: 0, size: pageSize }); },
    onActivateRow: (rowId: string) => { void handleActivateRow(rowId); if (activeSectionId === 'devices') writeLocation({ inspect: rowId }); },
    onCreate: handleCreate,
    onRefresh: () => void Promise.all([loadInventory(), mutateManagedService()]),
    onFieldChange: handleFieldChange,
    onSelectorSearch: (id: string, value: string) => setSelectorSearch(current => ({ ...current, [selectorKey(id)]: value })),
    onSelectorRetry: () => void Promise.all([
      sitesPage.mutate(),
      deviceTypesPage.mutate(),
      authProfilesPage.mutate(),
      sourceDevicesPage.mutate(),
      targetDevicesPage.mutate(),
      sourceInterfacesPage.mutate(),
      targetInterfacesPage.mutate(),
    ]),
    onSave: () => void handleSave(),
    onRequestDelete: deleteAllowed ? () => {
      setConfirmationError(null);
      setConfirmationKind('delete');
      setConfirmationOpen(true);
    } : undefined,
    onRequestCloseEditor: () => {
      if (editorDirty) {
        setConfirmationError(null);
        setConfirmationKind('discard-changes');
        setConfirmationOpen(true);
        return;
      }
      handleCancelEdit();
    },
    onConfirm: () => {
      if (confirmationKind === 'discard-changes') {
        setConfirmationOpen(false);
        handleCancelEdit();
        return;
      }
      void handleDelete();
    },
    onCancelConfirmation: () => {
      setConfirmationError(null);
      setConfirmationOpen(false);
    },
    onEditSelected: () => setTakeoverMode('editor'),
    onCollectionHealth: () => navigate('/status/operations/collection'),
    onDetailPageChange: setDetailPage,
    onDetailSearchChange: (value: string) => { setDetailSearch(value); setDetailPage(0); setSelectorSearch(current => ({ ...current, 'detail-records': value })); },
    onDetailTabChange: (tabId: string) => handleDetailTabChange(activeSectionId, tabId),
    onDetailRowActivate: (tabId: string, rowId: string) => handleDetailRowSelect(activeSectionId, tabId, rowId),
  };
}

function labelizeManagedService(value?: string): string {
  if (!value) {
    return 'Unknown';
  }
  return value
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function buildEditorFields(
  section: StatusInventorySectionId,
  draft: Draft,
  validation: Record<string, string>,
  sites: StatusSite[],
  deviceTypes: StatusDeviceType[],
  devices: StatusDevice[],
  interfaces: StatusInterface[],
  authProfiles: StatusSNMPAuthProfile[],
  readOnly = false,
  discoveryResult: StatusDeviceDiscoveryResult | null = null
): StatusInventoryEditorField[] {
  const siteOptions = sites.map(site => ({ value: site.site_id, label: site.display_name }));
  const deviceTypeOptions = deviceTypes.map(type => ({ value: type.device_type_id, label: [type.display_name, type.read_only ? 'System' : 'Tenant', type.vendor, type.model_match].filter(Boolean).join(' / ') }));
  const authOptions = authProfiles.map(profile => ({
    value: profile.auth_name,
    label: `${profile.display_name} / ${profile.auth_name}${profile.active ? '' : ' / Inactive'}`,
    disabled: !profile.active,
  }));
  const sourceDevices = devices.filter(device => !draft.source_site_id || device.site_id === draft.source_site_id);
  const targetDevices = devices.filter(device => !draft.target_site_id || device.site_id === draft.target_site_id);
  const sourceInterfaces = interfaces.filter(item => !draft.source_device_id || item.device_id === draft.source_device_id);
  const targetInterfaces = interfaces.filter(item => !draft.target_device_id || item.device_id === draft.target_device_id);

  if (section === 'entities') {
    return [];
  }

  const field = (id: string, label: string, kind: StatusInventoryEditorField['kind'] = 'text', options?: Array<{ value: string; label: string; disabled?: boolean }>, required = false): StatusInventoryEditorField => ({
    id,
    label,
    kind: ['community', 'snmp_community', 'auth_password', 'priv_password'].includes(id) ? 'password' : kind,
    value: draft[id] ?? '',
    options,
    error: validation[id],
    disabled: readOnly,
    required,
  });

  if (section === 'hubs') {
    return [
      field('slug', 'Slug', 'text', undefined, true),
      field('display_name', 'Display name', 'text', undefined, true),
      field('lat', 'Latitude', 'number', undefined, true),
      field('lng', 'Longitude', 'number', undefined, true),
      field('external_ref', 'External reference'),
      field('metadata', 'Metadata JSON', 'textarea'),
    ];
  }
  if (section === 'models') {
    return [
      field('slug', 'Slug', 'text', undefined, true),
      field('display_name', 'Display name', 'text', undefined, true),
      field('vendor', 'Vendor'),
      field('model_match', 'Model match / discovery selector'),
      field('default_monitoring_enabled', 'Default monitoring enabled', 'checkbox'),
      field('metadata', 'Metadata JSON', 'textarea'),
    ];
  }
  if (section === 'devices') {
    const discoveryEnabled = Boolean(draft.discovery_enabled);
    if (discoveryEnabled) {
      const fields = [
        field('discovery_enabled', 'Use SNMP discovery', 'checkbox'),
        field('hostname', 'Hostname', 'text', undefined, true),
        field('site_id', 'Hub', 'select', siteOptions, Boolean(discoveryResult)),
        field('management_address', 'Management address'),
        field('display_name', 'Display name'),
        field('device_role', 'Device role'),
        field('snmp_community', 'SNMP community', 'text', undefined, true),
        field('snmp_version', 'SNMP version'),
        field('snmp_port', 'SNMP port', 'number'),
        field('snmp_auth', 'SNMP auth profile', 'select', authOptions),
        field('snmp_walk_params', 'Alloy walk params name'),
        field('monitoring_enabled', 'Monitoring enabled', 'checkbox'),
        field('metadata', 'Metadata JSON', 'textarea'),
      ];
      if (discoveryResult) {
        fields.push({
          id: 'discovery_result',
          label: discoveryResult.matched ? 'Discovery result' : 'Discovery result: unsupported',
          kind: 'textarea',
          value: formatDiscoveryResult(discoveryResult),
          disabled: true,
        });
        fields.push(field('device_type_id', discoveryResult.matched ? 'Matched device model' : 'Custom model / overlay-backed model', 'select', deviceTypeOptions, !discoveryResult.matched));
      }
      return fields;
    }
    return [
      field('discovery_enabled', 'Use SNMP discovery', 'checkbox'),
      field('hostname', 'Hostname', 'text', undefined, true),
      field('display_name', 'Display name'),
      field('site_id', 'Hub', 'select', siteOptions, true),
      field('device_type_id', 'Device type', 'select', deviceTypeOptions),
      field('management_address', 'Management address'),
      field('device_role', 'Device role'),
      field('snmp_auth', 'SNMP auth profile', 'select', authOptions),
      field('snmp_walk_params', 'Alloy walk params name'),
      field('monitoring_enabled', 'Monitoring enabled', 'checkbox'),
      field('metadata', 'Metadata JSON', 'textarea'),
    ];
  }
  if (section === 'overlays') {
    return [
      field('device_type_id', 'Device model', 'select', deviceTypeOptions, true),
      field('overlay_name', 'Overlay name', 'text', undefined, true),
      field('module_name', 'Alloy module name', 'text', undefined, true),
      field('version', 'Version', 'number'),
      field('active', 'Active', 'checkbox'),
      field('overlay_config', 'Overlay config JSON', 'textarea', undefined, true),
    ];
  }
  if (section === 'auth') {
    return [
      field('auth_name', 'Auth name', 'text', undefined, true),
      field('display_name', 'Display name', 'text', undefined, true),
      field('snmp_version', 'SNMP version', 'number', undefined, true),
      field('community', 'Community / v1-v2c secret'),
      field('username', 'SNMPv3 username'),
      field('security_level', 'SNMPv3 security level'),
      field('auth_protocol', 'SNMPv3 auth protocol'),
      field('auth_password', 'SNMPv3 auth password'),
      field('priv_protocol', 'SNMPv3 privacy protocol'),
      field('priv_password', 'SNMPv3 privacy password'),
      field('active', 'Active', 'checkbox'),
      field('metadata', 'Metadata JSON', 'textarea'),
    ];
  }
  return [
    field('slug', 'Slug', 'text', undefined, true),
    field('display_name', 'Display name', 'text', undefined, true),
    field('link_kind', 'Link kind'),
    field('capacity_bps', 'Capacity bps', 'number'),
    field('monitoring_enabled', 'Monitoring enabled', 'checkbox'),
    field('source_site_id', 'Source hub', 'select', siteOptions, true),
    field('source_device_id', 'Source device', 'select', sourceDevices.map(device => ({ value: device.device_id, label: device.display_name || device.hostname }))),
    field('source_interface_id', 'Source interface', 'select', sourceInterfaces.map(item => ({ value: item.interface_id, label: item.name }))),
    field('target_site_id', 'Target hub', 'select', siteOptions, true),
    field('target_device_id', 'Target device', 'select', targetDevices.map(device => ({ value: device.device_id, label: device.display_name || device.hostname }))),
    field('target_interface_id', 'Target interface', 'select', targetInterfaces.map(item => ({ value: item.interface_id, label: item.name }))),
  ];
}

function validateDiscoveryProbeDraft(draft: Draft, authProfiles: StatusSNMPAuthProfile[]): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const key of ['hostname', 'snmp_community']) {
    if (!String(draft[key] ?? '').trim()) {
      errors[key] = 'Required.';
    }
  }
  if (draft.metadata && typeof draft.metadata === 'string') {
    try {
      JSON.parse(draft.metadata);
    } catch {
      errors.metadata = 'Metadata must be valid JSON.';
    }
  }
  validateMonitoredDeviceAuth(draft, authProfiles, errors);
  return errors;
}

function validateDiscoverySubmitDraft(
  draft: Draft,
  discoveryResult: StatusDeviceDiscoveryResult | null,
  authProfiles: StatusSNMPAuthProfile[]
): Record<string, string> {
  const errors = validateDiscoveryProbeDraft(draft, authProfiles);
  if (!String(draft.site_id ?? '').trim()) {
    errors.site_id = 'Required.';
  }
  if (!discoveryResult) {
    errors.discovery_result = 'Run discovery before creating the device.';
  }
  if (discoveryResult && !discoveryResult.matched && !String(draft.device_type_id ?? '').trim()) {
    errors.device_type_id = 'Select a custom model for unsupported devices.';
  }
  return errors;
}

function validateDraft(
  section: StatusInventorySectionId,
  draft: Draft,
  authProfiles: StatusSNMPAuthProfile[]
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const key of section === 'hubs'
    ? ['slug', 'display_name', 'lat', 'lng']
    : section === 'models'
      ? ['slug', 'display_name']
      : section === 'devices'
        ? Boolean(draft.discovery_enabled)
          ? ['hostname', 'site_id', 'snmp_community']
          : ['hostname', 'site_id']
      : section === 'auth'
        ? ['auth_name', 'display_name', 'snmp_version']
      : section === 'overlays'
        ? ['device_type_id', 'overlay_name', 'module_name', 'overlay_config']
      : ['slug', 'display_name', 'source_site_id', 'target_site_id']) {
    if (!String(draft[key] ?? '').trim()) {
      errors[key] = 'Required.';
    }
  }
  if (draft.metadata && typeof draft.metadata === 'string') {
    try {
      JSON.parse(draft.metadata);
    } catch {
      errors.metadata = 'Metadata must be valid JSON.';
    }
  }
  if (section === 'overlays' && typeof draft.overlay_config === 'string') {
    try {
      JSON.parse(draft.overlay_config);
    } catch {
      errors.overlay_config = 'Overlay config must be valid JSON.';
    }
  }
  if (section === 'devices') {
    validateMonitoredDeviceAuth(draft, authProfiles, errors);
  }
  return errors;
}

function validateMonitoredDeviceAuth(
  draft: Draft,
  authProfiles: StatusSNMPAuthProfile[],
  errors: Record<string, string>
): void {
  if (!Boolean(draft.monitoring_enabled)) {
    return;
  }
  const authName = String(draft.snmp_auth ?? '').trim();
  if (!authName) {
    errors.snmp_auth = 'Select an active SNMP auth profile before enabling monitoring.';
    return;
  }
  if (!authProfiles.some(profile => profile.active && profile.auth_name === authName)) {
    errors.snmp_auth = 'The selected SNMP auth profile is missing or inactive.';
  }
}

function buildPayload(section: StatusInventorySectionId, draft: Draft): Record<string, unknown> {
  if (section === 'hubs') {
    return {
      slug: draft.slug,
      display_name: draft.display_name,
      external_ref: draft.external_ref || undefined,
      location: { lat: Number(draft.lat), lng: Number(draft.lng) },
      metadata: parseMetadata(draft.metadata),
    };
  }
  if (section === 'models') {
    return {
      slug: draft.slug,
      display_name: draft.display_name,
      vendor: draft.vendor || undefined,
      model_match: draft.model_match || undefined,
      default_monitoring_enabled: Boolean(draft.default_monitoring_enabled),
      metadata: parseMetadata(draft.metadata),
    };
  }
  if (section === 'devices') {
    return {
      hostname: draft.hostname,
      display_name: draft.display_name || undefined,
      site_id: draft.site_id,
      device_type_id: draft.device_type_id || undefined,
      external_ref: draft.external_ref || undefined,
      management_address: draft.management_address || undefined,
      device_role: draft.device_role || undefined,
      vendor: draft.vendor || undefined,
      model: draft.model || undefined,
      monitoring_enabled: Boolean(draft.monitoring_enabled),
      metadata: withSNMPMetadata(parseMetadata(draft.metadata), draft),
    };
  }
  if (section === 'auth') {
    return {
      auth_name: draft.auth_name,
      display_name: draft.display_name,
      snmp_version: draft.snmp_version ? Number(draft.snmp_version) : 2,
      community: draft.community || undefined,
      username: draft.username || undefined,
      security_level: draft.security_level || undefined,
      auth_protocol: draft.auth_protocol || undefined,
      auth_password: draft.auth_password || undefined,
      priv_protocol: draft.priv_protocol || undefined,
      priv_password: draft.priv_password || undefined,
      active: Boolean(draft.active),
      metadata: parseMetadata(draft.metadata),
    };
  }
  if (section === 'overlays') {
    return {
      device_type_id: draft.device_type_id,
      overlay_name: draft.overlay_name,
      module_name: draft.module_name,
      overlay_config: parseMetadata(draft.overlay_config),
      version: draft.version ? Number(draft.version) : undefined,
      active: Boolean(draft.active),
    };
  }
  return {
    slug: draft.slug,
    display_name: draft.display_name,
    link_kind: draft.link_kind || undefined,
    capacity_bps: draft.capacity_bps ? Number(draft.capacity_bps) : undefined,
    monitoring_enabled: Boolean(draft.monitoring_enabled),
    source: {
      site_id: draft.source_site_id,
      device_id: draft.source_device_id || undefined,
      interface_id: draft.source_interface_id || undefined,
    },
    target: {
      site_id: draft.target_site_id,
      device_id: draft.target_device_id || undefined,
      interface_id: draft.target_interface_id || undefined,
    },
  };
}

function buildDiscoveryPayload(draft: Draft, opts: { previewOnly?: boolean; identity?: StatusDeviceDiscoveryResult['identity'] } = {}): StatusDeviceDiscoveryPayload {
  return {
    hostname: String(draft.hostname ?? ''),
    display_name: stringOrUndefined(draft.display_name),
    site_id: String(draft.site_id ?? ''),
    device_type_id: stringOrUndefined(draft.device_type_id),
    management_address: stringOrUndefined(draft.management_address),
    device_role: stringOrUndefined(draft.device_role),
    snmp_auth: stringOrUndefined(draft.snmp_auth),
    snmp_walk_params: stringOrUndefined(draft.snmp_walk_params),
    snmp_version: stringOrUndefined(draft.snmp_version),
    snmp_community: stringOrUndefined(draft.snmp_community),
    snmp_port: stringOrUndefined(draft.snmp_port),
    monitoring_enabled: Boolean(draft.monitoring_enabled),
    metadata: parseMetadata(draft.metadata),
    identity: opts.identity,
    preview_only: opts.previewOnly,
  };
}

function stringOrUndefined(value: unknown): string | undefined {
  const text = String(value ?? '').trim();
  return text ? text : undefined;
}

function parseMetadata(value: unknown): Record<string, unknown> {
  if (typeof value !== 'string' || !value.trim()) {
    return {};
  }
  return JSON.parse(value) as Record<string, unknown>;
}

function withSNMPMetadata(metadata: Record<string, unknown>, draft: Draft): Record<string, unknown> {
  const next = { ...metadata };
  const snmpAuth = String(draft.snmp_auth ?? '').trim();
  if (snmpAuth) {
    next.snmp_auth = snmpAuth;
  } else {
    delete next.snmp_auth;
  }
  const snmpWalkParams = String(draft.snmp_walk_params ?? '').trim();
  if (snmpWalkParams) {
    next.snmp_walk_params = snmpWalkParams;
  } else {
    delete next.snmp_walk_params;
  }
  return next;
}

function formatDiscoveryResult(result: StatusDeviceDiscoveryResult): string {
  return [
    `Status: ${result.matched ? 'Supported model matched' : 'No supported model matched'}`,
    `Model: ${result.device_type?.display_name ?? 'Select a custom model below'}`,
    `Scope: ${result.device_type?.read_only ? 'System catalog' : result.device_type ? 'Tenant catalog' : 'Not matched'}`,
    `sysObjectID: ${result.identity?.sys_object_id || 'Not returned'}`,
    `sysDescr: ${result.identity?.sys_descr || 'Not returned'}`,
    result.inventory ? `Interfaces discovered: ${result.inventory.interfaces_discovered}; components discovered: ${result.inventory.components_discovered ?? 0}; sensors discovered: ${result.inventory.sensors_discovered ?? 0} (${result.inventory.profile})` : '',
    ...(result.inventory?.warnings ?? []).map(warning => `Warning: ${warning}`),
  ].filter(Boolean).join('\n');
}

function buildDeviceDetail(
  device: StatusDevice,
  interfaces: StatusInterface[],
  components: StatusDeviceComponent[],
  activeTabId: string,
  selectedInterfaceId: string | null
): StatusInventorySectionDetail {
  const deviceInterfaces = interfaces
    .filter(item => item.device_id === device.device_id)
    .sort((a, b) => (a.ifindex ?? 0) - (b.ifindex ?? 0) || a.name.localeCompare(b.name));
  const selectedInterface = selectedInterfaceId ? deviceInterfaces.find(item => item.interface_id === selectedInterfaceId) ?? null : null;
  const deviceComponents = components
    .filter(item => item.device_id === device.device_id)
    .sort(sortComponents);
  const interfaceComponents = selectedInterface
    ? deviceComponents.filter(component => componentBelongsToInterface(component, selectedInterface))
    : [];

  return {
    title: device.display_name || device.hostname,
    description: 'Drill into discovered interfaces and the sensor/metric entities associated with this device.',
    activeTabId,
    tabs: [
      {
        id: 'interfaces',
        label: 'Interfaces',
        description: 'Select an interface to inspect related sensors and metrics.',
        columns: [
          { id: 'ifindex', label: 'ifIndex' },
          { id: 'description', label: 'Description' },
          { id: 'monitoring', label: 'Monitoring' },
          { id: 'related', label: 'Related data' },
        ],
        selectedRowId: selectedInterfaceId,
        emptyTitle: 'No interfaces discovered',
        emptyDescription: 'Interfaces appear after device discovery or Alloy-backed inventory sync.',
        rows: deviceInterfaces.map(item => ({
          id: item.interface_id,
          title: item.name,
          subtitle: item.description,
          badges: [{ id: 'monitoring', label: item.monitoring_enabled ? 'Monitoring on' : 'Monitoring off', tone: item.monitoring_enabled ? 'success' : 'warning' }],
          cells: {
            ifindex: item.ifindex !== undefined ? String(item.ifindex) : 'Not set',
            description: item.description ?? '',
            monitoring: item.monitoring_enabled ? 'On' : 'Off',
            related: String(deviceComponents.filter(component => componentBelongsToInterface(component, item)).length),
          },
          actionLabel: 'View',
          actionDescription: `View data related to ${item.name}`,
        })),
      },
      {
        id: 'interface-entities',
        label: selectedInterface ? `Interface Data (${interfaceComponents.length})` : 'Interface Data',
        description: selectedInterface
          ? `Sensors and metric entities related to ${selectedInterface.name}.`
          : 'Select an interface first to filter related sensors and metrics.',
        columns: componentColumns(),
        rows: interfaceComponents.map(componentRow),
        emptyTitle: selectedInterface ? 'No related data found' : 'Select an interface',
        emptyDescription: selectedInterface
          ? 'This interface has no linked or name-matched component/sensor rows yet.'
          : 'Open the Interfaces tab and choose an interface to drill into its related data.',
      },
      {
        id: 'device-entities',
        label: `All Entities (${deviceComponents.length})`,
        description: 'All components, sensors, optics, fans, power supplies, ports, and other entities materialized for this device.',
        columns: componentColumns(),
        rows: deviceComponents.map(componentRow),
        emptyTitle: 'No entities materialized',
        emptyDescription: 'Entity rows appear after Alloy emits tagged component/sensor samples and the materializer persists them.',
      },
    ],
  };
}

function componentColumns() {
  return [
    { id: 'key', label: 'Key' },
    { id: 'kind', label: 'Kind' },
    { id: 'sensor', label: 'Sensor' },
    { id: 'value', label: 'Current value' },
    { id: 'status', label: 'Status' },
  ];
}

function componentRow(component: StatusDeviceComponent) {
  const hasSensor = isSensorComponent(component);
  return {
    id: component.component_id,
    title: component.name || component.component_key,
    subtitle: component.component_key,
    badges: [
      { id: 'monitoring', label: component.monitoring_enabled ? 'Monitoring on' : 'Monitoring off', tone: component.monitoring_enabled ? 'success' as const : 'warning' as const },
      ...(hasSensor ? [{ id: 'sensor', label: component.sensor_type || 'Sensor', tone: 'info' as const }] : []),
    ],
    cells: {
      key: component.component_key,
      kind: component.component_kind || 'Component',
      sensor: component.sensor_type || 'Not a sensor',
      value: formatSensorValue(component),
      status: formatSensorStatus(component.sensor_oper_status),
    },
    actionLabel: 'View',
    actionDescription: `View ${component.name || component.component_key}`,
  };
}

function sortComponents(a: StatusDeviceComponent, b: StatusDeviceComponent): number {
  const sensorRank = Number(isSensorComponent(b)) - Number(isSensorComponent(a));
  if (sensorRank !== 0) {
    return sensorRank;
  }
  return (a.name || a.component_key).localeCompare(b.name || b.component_key);
}

function componentBelongsToInterface(component: StatusDeviceComponent, iface: StatusInterface): boolean {
  if (component.interface_id) {
    return component.interface_id === iface.interface_id;
  }
  const componentName = normalizeInventoryKey([component.name, component.component_key].filter(Boolean).join(' '));
  const candidates = [iface.name, iface.description].map(normalizeInventoryKey).filter(candidate => candidate.length >= 4);
  return candidates.some(candidate => compactContainsInventoryCandidate(componentName, candidate));
}

function normalizeInventoryKey(value: unknown): string {
  return String(value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function compactContainsInventoryCandidate(value: string, candidate: string): boolean {
  let offset = value.indexOf(candidate);
  while (offset !== -1) {
    const next = value[offset + candidate.length];
    if (!/\d/.test(next ?? '')) {
      return true;
    }
    offset = value.indexOf(candidate, offset + 1);
  }
  return false;
}

function endpointLabel(endpoint: StatusLinkConfig['source'], sites: StatusSite[], devices: StatusDevice[], interfaces: StatusInterface[]): string {
  const site = endpoint.site_label || sites.find(item => item.site_id === endpoint.site_id)?.display_name || 'Unknown hub';
  const device = endpoint.device_id ? devices.find(item => item.device_id === endpoint.device_id) : null;
  const iface = endpoint.interface_id ? interfaces.find(item => item.interface_id === endpoint.interface_id) : null;
  return [
    site,
    endpoint.device_label || device?.display_name || device?.hostname,
    endpoint.interface_label || iface?.name,
  ].filter(Boolean).join(' / ');
}

function isLinkSuppressed(link: StatusLinkConfig): boolean {
  return link.suppressed === true || link.lifecycle === 'suppressed';
}

function isSensorComponent(component: StatusDeviceComponent): boolean {
  return Boolean(component.sensor_type || component.sensor_value !== undefined || component.sensor_oper_status !== undefined);
}

function formatSensorValue(component: StatusDeviceComponent): string {
  if (component.sensor_normalized_value !== undefined && component.sensor_normalized_value !== null) {
    const formatted = Number.isInteger(component.sensor_normalized_value)
      ? String(component.sensor_normalized_value)
      : component.sensor_normalized_value.toFixed(5).replace(/0+$/, '').replace(/\.$/, '');
    return `${formatted}${component.sensor_normalized_unit || ''}`;
  }
  if (component.sensor_value === undefined || component.sensor_value === null) {
    return 'Not set';
  }
  const precision = component.sensor_precision ?? 0;
  const divisor = precision > 0 ? 10 ** precision : 1;
  const value = component.sensor_value / divisor;
  const formatted = Number.isInteger(value) ? String(value) : value.toFixed(Math.min(Math.max(precision, 0), 5)).replace(/0+$/, '').replace(/\.$/, '');
  const unit = sensorUnitLabel(component);
  return [formatted, unit].filter(Boolean).join(' ');
}

function sensorUnitLabel(component: StatusDeviceComponent): string {
  if (component.sensor_unit) {
    return component.sensor_unit;
  }
  switch ((component.sensor_type ?? '').toLowerCase()) {
    case 'dbm':
      return 'dBm';
    case 'temperature':
      return 'C';
    case 'voltage':
      return 'V';
    case 'current':
      return 'A';
    case 'power':
      return 'W';
    case 'rpm':
      return 'RPM';
    default:
      return '';
  }
}

function formatSensorStatus(status: number | undefined): string {
  if (status === undefined || status === null) {
    return 'Not set';
  }
  const label = {
    1: 'ok',
    2: 'unavailable',
    3: 'nonoperational',
  }[status];
  return label ? `${status} (${label})` : String(status);
}

function fieldKey(field: string): string {
  return field
    .replace(/^source\./, 'source_')
    .replace(/^target\./, 'target_')
    .replace('location.lat', 'lat')
    .replace('location.lng', 'lng');
}
